import { observer } from 'mobx-react-lite';
import React, { useState, useEffect } from 'react';

import { Icon, IconClose, IconCalendar } from '@flixbus/honeycomb-icons-react';
import { Calendar, Input, Button } from '@flixbus/honeycomb-react';

import { useOutsideClick } from 'hooks/useClickOutside';
import { rideFilterStore } from 'pages/RideListPage/components/RideFilters/RideFilterStore';
import { CALENDAR_MONTHS, CALENDAR_WEEKDAYS } from 'utils/calendarConfig';

import { EMPTY_SELECTION, reduceSelection } from './dateRangeSelection';
import { formatDateRangeLabel } from './formatDateRangeLabel';
import { DATERANGE_FILTER_TEXT } from './texts';

import styles from './styles.module.css';

const DaterangeFilterRides = observer(() => {
	const [showCalendar, setShowCalendar] = useState(false);
	const [{ startDate, endDate, isInSelection }, setSelection] = useState(EMPTY_SELECTION);

	const ref = useOutsideClick(() => resetCalendar());

	const today = new Date();
	const endMonth = new Date(today.getFullYear(), today.getMonth() + 12, 0);

	const { removeFilter, handleDateRangeChange } = rideFilterStore;
	const [valueShown, setValueShown] = useState('');
	const dateRangeValue = rideFilterStore.filtersMap.get('departureRange')?.value;

	useEffect(() => {
		setValueShown(formatDateRangeLabel(dateRangeValue));
		if (!dateRangeValue) {
			setSelection(EMPTY_SELECTION);
		}
	}, [dateRangeValue]);

	useEffect(() => {
		const isReadyForSubmit = startDate !== null && endDate !== null && isInSelection === false;
		if (isReadyForSubmit) {
			resetCalendar();
			setSelection(EMPTY_SELECTION);
			handleDateRangeChange({
				from: startDate,
				to: endDate,
			});
		}
	}, [startDate, endDate, isInSelection, handleDateRangeChange]);

	const resetCalendar = () => {
		setShowCalendar(false);
	};

	const cleanData = () => {
		removeFilter('departureRange');
	};

	const handleSelection = (date) => {
		setSelection((prev) => reduceSelection(prev, date));
	};

	const activateCalendar = () => {
		setShowCalendar(true);
	};

	const inputProps = {
		label: DATERANGE_FILTER_TEXT.inputLabel,
		placeholder: DATERANGE_FILTER_TEXT.placeholder,
		id: 'departure-range-input',
		onFocus: () => {
			activateCalendar();
		},
		iconLeft: (
			<Button onClick={activateCalendar}>
				<Icon extraClasses={styles.icon} InlineIcon={IconCalendar} />
			</Button>
		),
	};

	if (dateRangeValue) {
		inputProps.iconRight = (
			<Button
				extraClasses={styles.icon}
				display="square"
				onClick={() => {
					resetCalendar();
					cleanData();
				}}
			>
				<Icon extraClasses={styles.icon} InlineIcon={IconClose} />
			</Button>
		);
	}

	return (
		<div className={styles.holder} ref={ref}>
			<Input
				value={valueShown}
				{...inputProps}
				readOnly
				arial-label={DATERANGE_FILTER_TEXT.inputAriaLabel}
			/>
			{showCalendar && (
				<div className={styles.calendar}>
					<Calendar
						arial-label={DATERANGE_FILTER_TEXT.calendarAriaLabel}
						id="simple-picker"
						selected={undefined}
						startDate={new Date(2024, 1, 1)}
						endDate={endMonth}
						handleSelect={handleSelection}
						appearance="compact"
						days={CALENDAR_WEEKDAYS}
						months={CALENDAR_MONTHS}
						startSelected={startDate}
						endSelected={endDate}
						showCurrentDate
					/>
				</div>
			)}
		</div>
	);
});
export default DaterangeFilterRides;
